/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useInjectSaga } from '../../utils/injectSaga';
import { useInjectReducer } from '../../utils/injectReducer';
import saga from './saga';
import reducer from './reducer';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../components/data-grid';
import {
	getConfigsSelector,
	getIsConfigsLoadingSelector,
	getIsPlanningsProcessListLoadingSelector,
	getPlanningsProcessListSelector,
	getPaginationSelector,
	getIsModalShownSelector,
	getActiveProcessIdSelector,
	getProcessInfoSelector,
	getIsProcessInfoLoadingSelector,
	getIsPlanningSelectedSelector,
	getModesSelector,
	getIsModesLoadingSelector,
} from './selectors';
import {
	getConfigsAction,
	getProcessInfoAction,
	getPlanningsProcessListAction,
	resetStateAction,
	getModesAction,
} from './actions';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { TABLE_PLANNINGS_PROCESS_COLUMNS } from './constants';
import closeModal from '../../utils/closeModal';
import { PlanningModalComponent } from '../orders-groups/components/planning-modal';
import { ModalHeader } from './components/modal-header';
import { CollectionComponent } from './components/collection';

const PlanningsProcessContainerWrapper = ({
	onGetPlanningsProcessList,
	planningsProcessList,
	isPlanningsProcessListLoading,
	onGetProcessInfo,
	processInfo,
	isProcessInfoLoading,
	onResetState,
	onGetConfigs,
	configs,
	isConfigsLoading,
	activeProcessId,
	isModalShown,
	isPlanningSelected,
	onGetModes,
	modes,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'planningsProcessSaga', saga });
	useInjectReducer({ key: 'planningsProcessContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		if (isModalShown && activeProcessId) {
			onGetPlanningsProcessList({ process_id: activeProcessId, searchQuery: removeModalQuery(query.toString()) });
			onGetProcessInfo({ process_id: activeProcessId });
			onGetConfigs();
		}

		onGetModes();

		return () => onResetState();
	}, [isModalShown, activeProcessId]);

	const [isPlanningModalShown, setIsPlanningModalShown] = useState(false);

	const handleModalClose = () => {
		closeModal(query);
		onResetState();
		history.push(`${pathname}?${query}`);
	};

	const handleAddButtonClick = () => {
		setIsPlanningModalShown(true);
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		onGetPlanningsProcessList({ process_id: activeProcessId, searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(
				normalizeDataGridModel(filterModel, TABLE_PLANNINGS_PROCESS_COLUMNS(configs)),
			);

			query.set('filters', jsonFilters);

			onGetPlanningsProcessList({ process_id: activeProcessId, searchQuery: removeModalQuery(query.toString()) });
		}
	});

	const getDetailCollectionPanelContent = useCallback(row => {
		if (row?.row?.status?.id === 'warning') {
			return <CollectionComponent row={row.row} modes={modes} />;
		}
		return null;
	});

	if (isPlanningModalShown) {
		return (
			<PlanningModalComponent
				isModalShown={isPlanningModalShown}
				setIsModalShown={setIsPlanningModalShown}
				process={processInfo}
			/>
		);
	}

	return (
		<ModalWindow
			onClose={handleModalClose}
			isModalShown
			modalContent={
				<DataGridComponent
					columns={TABLE_PLANNINGS_PROCESS_COLUMNS(configs)}
					rows={planningsProcessList}
					isTableDataLoading={isPlanningsProcessListLoading || isConfigsLoading || isProcessInfoLoading}
					isWithoutHeader
					isChildren
					isWithoutFooter
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
					getDetailPanelContent={getDetailCollectionPanelContent}
					detailPanelHeight={200}
					filterKey="childrenFilters"
					sortKey="childrenSorts"
					rowHeight={45}
				/>
			}
			modalHeader={
				<ModalHeader
					onAddButtonClick={handleAddButtonClick}
					onModalClose={handleModalClose}
					title={processInfo?.title}
				/>
			}
			footerButtons={
				<ModalFooterButtons
					rightButtons={[
						{
							onClick: handleModalClose,
							text: 'Закрыть',
							color: 'secondary',
							disabled: !!isPlanningSelected,
						},
					]}
				/>
			}
			style={{ width: '1200px' }}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	activeProcessId: getActiveProcessIdSelector(),
	planningsProcessList: getPlanningsProcessListSelector(),
	isPlanningsProcessListLoading: getIsPlanningsProcessListLoadingSelector(),
	processInfo: getProcessInfoSelector(),
	isProcessInfoLoading: getIsProcessInfoLoadingSelector(),
	pagination: getPaginationSelector(),
	configs: getConfigsSelector(),
	isConfigsLoading: getIsConfigsLoadingSelector(),
	isModalShown: getIsModalShownSelector(),
	isPlanningSelected: getIsPlanningSelectedSelector(),
	modes: getModesSelector(),
	isModesLoading: getIsModesLoadingSelector(),
});

const mapDispatchToProps = {
	onGetPlanningsProcessList: getPlanningsProcessListAction,
	onGetProcessInfo: getProcessInfoAction,
	onGetConfigs: getConfigsAction,
	onGetModes: getModesAction,
	onResetState: resetStateAction,
};

PlanningsProcessContainerWrapper.propTypes = {
	activeProcessId: PropTypes.string,
	isModalShown: PropTypes.bool.isRequired,
	onGetPlanningsProcessList: PropTypes.func.isRequired,
	planningsProcessList: PropTypes.array.isRequired,
	isPlanningsProcessListLoading: PropTypes.bool.isRequired,
	onGetProcessInfo: PropTypes.func.isRequired,
	processInfo: PropTypes.object,
	isProcessInfoLoading: PropTypes.bool.isRequired,
	onGetConfigs: PropTypes.func.isRequired,
	configs: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isConfigsLoading: PropTypes.bool.isRequired,
	onResetState: PropTypes.func,
	isPlanningSelected: PropTypes.any,
	onGetModes: PropTypes.func.isRequired,
	modes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isModesLoading: PropTypes.bool.isRequired,
};

const PlanningsProcessContainer = connect(mapStateToProps, mapDispatchToProps)(PlanningsProcessContainerWrapper);

export { PlanningsProcessContainer };
